import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowIosDownward, CheckMark } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Avatar, Stack } from '@lyearn/ui';
import useUser from '@/components/Form/components/Conditions/hooks/useUser';
import { PopperOverride } from '@/components/PopperOverride';
import UserSearch from '@/components/UsersSearch';
import UserDetail, { UserDetailsOverrides } from '@/components/UsersSearch/components/UserDetail';
import UserEntity from '@/entity/User';
import { UserType } from '@/types/schema';

import Loader from '../../Loader';
import { filterValueStyle } from '../styles';
import { UserFilterProps } from '../types';

const DEFAULT_SIZE = 'medium';

const USER_DETAIL_CLASS_OVERRIDES = {
  medium: {
    Email: {
      Typography: {
        variant: 'body-short-02',
      },
    },
    Name: {
      Typography: {
        variant: 'body-short-02',
      },
    },
    Icon: {
      className: 'me-8',
      size: 20,
    },
  } as UserDetailsOverrides,
  large: {
    Email: {
      Typography: {
        variant: 'body-short-01',
      },
    },
    Name: {
      Typography: {
        variant: 'body-short-01',
      },
    },
    Icon: {
      className: 'me-8',
      size: 24,
    },
  } as UserDetailsOverrides,
};

const USER_DETAIL_EMAIL_CLASS_OVERRIDES = {
  medium: {
    Email: {
      Typography: {
        variant: 'body-short-03',
        color: 'text-text-secondary',
      },
    },
    Name: {
      Typography: {
        variant: 'body-short-02',
        color: 'text-text-primary',
      },
    },
    Icon: {
      className: 'me-12',
      size: 32,
    },
  } as UserDetailsOverrides,
  large: {
    Email: {
      Typography: {
        variant: 'body-short-03',
        color: 'text-text-secondary',
      },
    },
    Name: {
      Typography: {
        variant: 'body-short-01',
        color: 'text-text-primary',
      },
    },
    Icon: {
      className: 'me-16',
      size: 40,
    },
  } as UserDetailsOverrides,
};

function UserFilter(props: UserFilterProps) {
  const { t } = useTranslation();
  const {
    filterValue,
    onFilterValueChange,
    disabled,
    error,
    className = filterValueStyle,
    placeholder,
    size = DEFAULT_SIZE,
    clearOnBlur,
    groupIds,
    showEmail = false,
    crossSubWorkspace,
    classes,
    excludeUsers,
  } = props;
  const { user: selectedUser, fetching, stale } = useUser(filterValue, !filterValue, true);
  const divRef = React.useRef(null);
  const onSelect = useCallback(
    (userId?: string) => {
      onFilterValueChange(userId);
    },
    [onFilterValueChange],
  );
  const hasError = useMemo(
    () => error && !disabled && !filterValue,
    [disabled, error, filterValue],
  );

  // todo.. refactor @HimanshuSahu31
  const startAdornment = useCallback(
    () => (
      <Avatar
        name={selectedUser?.name!}
        size={size === DEFAULT_SIZE ? 'x-small' : 'small'}
        src={selectedUser?.profilePicture!}
      />
    ),
    [selectedUser?.name, selectedUser?.profilePicture, size],
  );

  if (fetching || stale) {
    return <Loader className={cn(className, size === 'large' ? '!h-48' : '!h-40')} />;
  }

  return (
    <Stack
      ref={divRef}
      className={cn(className, {
        'cursor-not-allowed': disabled,
      })}
      direction="col">
      <UserSearch
        PopperComponent={(val) => <PopperOverride placement="bottom-end" {...val} />}
        className="w-full"
        classes={classes}
        clearOnBlur={clearOnBlur}
        closeIcon={null}
        crossSubWorkspace={crossSubWorkspace}
        disabled={disabled}
        error={hasError}
        excludedUsers={excludeUsers}
        groupIds={groupIds}
        initalValue={selectedUser ? UserEntity.getName(selectedUser) : undefined}
        placeholder={placeholder ?? t('Group:Editor.Value')}
        popupIcon={<ArrowIosDownward className="h-20 w-20" fillColorToken="icon-placeholder" />}
        renderEndAdornment={() => null}
        renderOption={(user: UserType) => {
          const isSelected = user.id === filterValue;
          const classOverrides = showEmail
            ? USER_DETAIL_EMAIL_CLASS_OVERRIDES[size]
            : USER_DETAIL_CLASS_OVERRIDES[size];
          const iconSize = showEmail ? '24' : classOverrides.Icon?.size;

          return (
            <UserDetail
              key={user.id}
              className="mb-2 flex-shrink-0 py-10 px-12"
              endAdornment={
                isSelected ? (
                  <CheckMark
                    className={`w-${iconSize} h-${iconSize}`}
                    fillColorToken="icon-active"
                  />
                ) : null
              }
              isSelected={isSelected}
              overrides={classOverrides}
              showEmail={showEmail}
              user={user}
            />
          );
        }}
        renderStartAdornment={startAdornment}
        size={size}
        onChange={onSelect}
      />
    </Stack>
  );
}

export default UserFilter;
